/**
 * MicInput.tsx
 *
 * Reusable microphone-recording component that posts to /api/transcribe and
 * delivers the transcript via callback. Drop next to any text input.
 *
 * Public API is intentionally minimal:
 *   <MicInput onTranscript={(text) => setValue(prev => prev + ' ' + text)} />
 *
 * Internal state machine:
 *   idle → requesting → recording → uploading → idle  (happy path)
 *                  ↓ denied        ↓ failed
 *                error             error  (with retry)
 *
 * Browser compatibility:
 *   - Chrome / Firefox  → audio/webm (Opus codec)
 *   - Safari (desktop)  → audio/mp4  (AAC)
 *   - iOS Safari        → audio/mp4  (AAC)
 * MediaRecorder.isTypeSupported() chosen at recording time.
 *
 * Auth:
 *   Uses the same JWT bearer pattern as the rest of V2 — reads
 *   `distill_auth_token` or `distill_guest_token` from localStorage.
 *   Surfaces a clear error if neither is present (no silent send-without-header).
 */

import React, { useEffect, useRef, useState } from "react";
import axios from "axios";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:8085";

// ── Types ────────────────────────────────────────────────────────────────────

type State =
  | "idle"
  | "requesting"
  | "recording"
  | "uploading"
  | "error";

export interface MicInputProps {
  /** Called with the transcribed text when the request succeeds. */
  onTranscript: (text: string) => void;
  /** Hard cap for recording duration. Defaults to 60 seconds. */
  maxSeconds?: number;
  /** Disable the button. The component will not start recording. */
  disabled?: boolean;
  /** Visual size — defaults to 'md'. */
  size?: "sm" | "md" | "lg";
  /** Optional className passthrough for layout overrides. */
  className?: string;
  /** Optional inline style passthrough for layout overrides. */
  style?: React.CSSProperties;
  /** Optional accessible label override. */
  ariaLabel?: string;
}

// ── Helpers ──────────────────────────────────────────────────────────────────

/** Pick the best MIME type the browser supports for our use case. */
function pickRecorderMimeType(): string {
  if (typeof MediaRecorder === "undefined") return "audio/webm";
  const candidates = [
    "audio/webm;codecs=opus",
    "audio/webm",
    "audio/mp4",         // Safari
    "audio/ogg;codecs=opus",
  ];
  for (const c of candidates) {
    if (MediaRecorder.isTypeSupported(c)) return c;
  }
  return "audio/webm";   // last-resort fallback
}

/** Strip codec info from a full MIME type so the multer whitelist accepts it. */
function baseMime(full: string): string {
  return full.split(";")[0].trim();
}

/** Read the JWT (real or guest) from localStorage — same pattern as DistillPageV2. */
function authHeader(): Record<string, string> {
  const token =
    localStorage.getItem("distill_auth_token") ||
    localStorage.getItem("distill_guest_token");
  return token ? { Authorization: `Bearer ${token}` } : {};
}

// ── Sizing tokens ────────────────────────────────────────────────────────────

const SIZES = {
  sm: { btn: 28, icon: 14, font: 11 },
  md: { btn: 36, icon: 18, font: 12 },
  lg: { btn: 44, icon: 22, font: 13 },
} as const;

// ── Component ────────────────────────────────────────────────────────────────

const MicInput: React.FC<MicInputProps> = ({
  onTranscript,
  maxSeconds = 60,
  disabled = false,
  size = "md",
  className,
  style,
  ariaLabel,
}) => {
  const [state, setState]           = useState<State>("idle");
  const [errorMsg, setErrorMsg]     = useState<string | null>(null);
  const [secondsLeft, setSecondsLeft] = useState<number>(maxSeconds);

  // Refs that need to survive re-renders without triggering them
  const streamRef     = useRef<MediaStream | null>(null);
  const recorderRef   = useRef<MediaRecorder | null>(null);
  const chunksRef     = useRef<Blob[]>([]);
  const mimeRef       = useRef<string>("audio/webm");
  const countdownRef  = useRef<number | null>(null);
  const stopTimerRef  = useRef<number | null>(null);
  const onTranscriptRef = useRef(onTranscript);

  // Keep the latest onTranscript in a ref so the recorder.onstop closure isn't stale
  useEffect(() => { onTranscriptRef.current = onTranscript; });

  // ── Cleanup on unmount ──────────────────────────────────────────────────
  useEffect(() => {
    return () => {
      cleanupRecording();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /** Stop all media tracks + clear timers. Safe to call multiple times. */
  const cleanupRecording = () => {
    if (countdownRef.current !== null) {
      window.clearInterval(countdownRef.current);
      countdownRef.current = null;
    }
    if (stopTimerRef.current !== null) {
      window.clearTimeout(stopTimerRef.current);
      stopTimerRef.current = null;
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop());
      streamRef.current = null;
    }
    recorderRef.current = null;
    chunksRef.current = [];
  };

  /** Send the recorded audio blob to /api/transcribe and call onTranscript. */
  const uploadBlob = async (blob: Blob, mime: string) => {
    setState("uploading");
    try {
      const form = new FormData();
      // Field name MUST match the backend route's `upload.single('audio')`.
      // Some servers care about the filename extension — use a sensible one.
      const ext = mime.includes("mp4") ? "mp4" : mime.includes("ogg") ? "ogg" : "webm";
      form.append("audio", blob, `recording.${ext}`);

      const res = await axios.post(`${API_URL}/api/transcribe`, form, {
        headers: { ...authHeader() },
        // axios infers Content-Type: multipart/form-data with boundary
      });

      const transcript: string = res.data?.transcript ?? "";
      if (!transcript.trim()) {
        console.warn("[MicInput] empty transcript returned from server");
        setErrorMsg("Couldn't make out any speech. Try again?");
        setState("error");
        return;
      }

      console.log("[MicInput] ✅ transcript", transcript.length, "chars");
      onTranscriptRef.current(transcript);
      setState("idle");
      setErrorMsg(null);
    } catch (e: any) {
      const status = e?.response?.status;
      const code   = e?.response?.data?.error;
      console.error("[MicInput] upload failed:", status, code, e?.message);
      // Surface a friendly message — keep the silent-failure-defense lesson alive.
      if (status === 401)      setErrorMsg("Sign-in expired. Refresh the page.");
      else if (status === 413) setErrorMsg("Recording too long. Keep it under 1 minute.");
      else if (status === 415) setErrorMsg("Audio format not supported on this browser.");
      else                      setErrorMsg("Couldn't transcribe. Tap to retry.");
      setState("error");
    }
  };

  /** Stop the recorder synchronously and let onstop deal with the blob. */
  const stopRecording = () => {
    if (stopTimerRef.current !== null) {
      window.clearTimeout(stopTimerRef.current);
      stopTimerRef.current = null;
    }
    if (countdownRef.current !== null) {
      window.clearInterval(countdownRef.current);
      countdownRef.current = null;
    }
    const r = recorderRef.current;
    if (r && r.state !== "inactive") {
      try { r.stop(); } catch { /* already stopping */ }
    }
  };

  /** Start the full record flow: permission → recorder → countdown → autostop. */
  const startRecording = async () => {
    if (disabled) return;
    if (state === "recording" || state === "uploading" || state === "requesting") return;

    setErrorMsg(null);
    setSecondsLeft(maxSeconds);
    setState("requesting");

    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      setErrorMsg("Microphone not available in this browser.");
      setState("error");
      return;
    }

    let stream: MediaStream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl:  true,
        },
      });
    } catch (e: any) {
      console.warn("[MicInput] getUserMedia denied/failed:", e?.name, e?.message);
      setErrorMsg(
        e?.name === "NotAllowedError"
          ? "Microphone access denied. Enable it in browser settings."
          : "Couldn't access the microphone."
      );
      setState("error");
      return;
    }
    streamRef.current = stream;

    const mime = pickRecorderMimeType();
    mimeRef.current = mime;
    let recorder: MediaRecorder;
    try {
      recorder = new MediaRecorder(stream, { mimeType: mime });
    } catch (e: any) {
      console.error("[MicInput] MediaRecorder construction failed:", e?.message);
      cleanupRecording();
      setErrorMsg("Recording not supported in this browser.");
      setState("error");
      return;
    }
    recorderRef.current = recorder;
    chunksRef.current = [];

    recorder.ondataavailable = ev => {
      if (ev.data && ev.data.size > 0) chunksRef.current.push(ev.data);
    };

    recorder.onstop = () => {
      const blob = new Blob(chunksRef.current, { type: mime });
      const tracks = streamRef.current?.getTracks() ?? [];
      tracks.forEach(t => t.stop());
      streamRef.current = null;
      // If we never captured anything, treat as error — likely permission issue.
      if (blob.size === 0) {
        setErrorMsg("No audio captured. Try again.");
        setState("error");
        return;
      }
      // Strip the codec suffix so the backend MIME whitelist accepts it.
      uploadBlob(blob, baseMime(mime));
    };

    recorder.onerror = ev => {
      console.error("[MicInput] recorder error", ev);
      setErrorMsg("Recording error. Try again.");
      setState("error");
      cleanupRecording();
    };

    // Start recording. Timeslice keeps memory low + ondataavailable fires periodically.
    try {
      recorder.start(250);
    } catch (e: any) {
      console.error("[MicInput] recorder.start failed:", e?.message);
      cleanupRecording();
      setErrorMsg("Couldn't start recording.");
      setState("error");
      return;
    }

    setState("recording");

    // Countdown timer (1 Hz) for visual + auto-stop timer (single shot).
    countdownRef.current = window.setInterval(() => {
      setSecondsLeft(s => Math.max(0, s - 1));
    }, 1000);
    stopTimerRef.current = window.setTimeout(() => {
      stopRecording();
    }, maxSeconds * 1000);
  };

  // ── Click handler (depending on state) ──────────────────────────────────
  const onClick = () => {
    if (disabled) return;
    if (state === "idle" || state === "error") {
      startRecording();
    } else if (state === "recording") {
      stopRecording();
    }
    // requesting / uploading: ignore clicks (button shows spinner)
  };

  // ── Visual ──────────────────────────────────────────────────────────────
  const sz = SIZES[size];
  const isRecording = state === "recording";
  const isWorking   = state === "requesting" || state === "uploading";
  const colors = {
    idle:       { bg: "#fff",      fg: "#5B4FE8", border: "1.5px solid #E4E4E0" },
    requesting: { bg: "#fff",      fg: "#999",    border: "1.5px solid #E4E4E0" },
    recording:  { bg: "#E74C3C",   fg: "#fff",    border: "1.5px solid #C0392B" },
    uploading:  { bg: "#fff",      fg: "#5B4FE8", border: "1.5px solid #E4E4E0" },
    error:      { bg: "#FFF6F4",   fg: "#C0392B", border: "1.5px solid #F0B5AB" },
  }[state];

  const titleByState: Record<State, string> = {
    idle:       "Click to record (max 1 min)",
    requesting: "Requesting microphone…",
    recording:  `Recording — click to stop (${secondsLeft}s left)`,
    uploading:  "Transcribing…",
    error:      errorMsg ?? "Try again",
  };

  return (
    <div
      className={className}
      style={{ display: "inline-flex", flexDirection: "column", alignItems: "flex-start", gap: 4, ...style }}
    >
      <button
        type="button"
        onClick={onClick}
        disabled={disabled || isWorking}
        title={titleByState[state]}
        aria-label={ariaLabel ?? titleByState[state]}
        data-mic-state={state}
        style={{
          width:           sz.btn,
          height:          sz.btn,
          borderRadius:    "50%",
          background:      colors.bg,
          color:           colors.fg,
          border:          colors.border,
          cursor:          disabled || isWorking ? "default" : "pointer",
          display:         "flex",
          alignItems:      "center",
          justifyContent:  "center",
          padding:         0,
          flexShrink:      0,
          transition:      "background 0.15s, color 0.15s, border-color 0.15s",
          boxShadow:       isRecording ? "0 0 0 4px rgba(231,76,60,0.18)" : "none",
          animation:       isRecording ? "micPulse 1.2s ease-in-out infinite" : "none",
        }}
      >
        <style>{`@keyframes micPulse { 0%,100% { box-shadow: 0 0 0 4px rgba(231,76,60,0.18); } 50% { box-shadow: 0 0 0 8px rgba(231,76,60,0.10); } }`}</style>

        {/* Idle / error: mic glyph */}
        {(state === "idle" || state === "error") && (
          <svg width={sz.icon} height={sz.icon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M12 2a3 3 0 0 0-3 3v6a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3z"/>
            <path d="M19 10v1a7 7 0 0 1-14 0v-1"/>
            <line x1="12" y1="19" x2="12" y2="23"/>
            <line x1="8"  y1="23" x2="16" y2="23"/>
          </svg>
        )}

        {/* Recording: filled square (stop) */}
        {state === "recording" && (
          <svg width={sz.icon} height={sz.icon} viewBox="0 0 24 24" fill="currentColor">
            <rect x="6" y="6" width="12" height="12" rx="2" />
          </svg>
        )}

        {/* Requesting / uploading: spinner */}
        {(state === "requesting" || state === "uploading") && (
          <svg width={sz.icon} height={sz.icon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" style={{ animation: "micSpin 0.9s linear infinite" }}>
            <style>{`@keyframes micSpin { from {transform:rotate(0)} to {transform:rotate(360deg)} }`}</style>
            <path d="M21 12a9 9 0 1 1-6.2-8.55" />
          </svg>
        )}
      </button>

      {/* Inline status row — countdown when recording, error message on failure. */}
      {(isRecording || state === "error") && (
        <span
          data-mic-status
          style={{
            fontSize:   sz.font,
            fontWeight: 600,
            color:      isRecording ? "#C0392B" : "#C0392B",
            fontFamily: "'Bricolage Grotesque', sans-serif",
            letterSpacing: "0.01em",
            lineHeight:  1.2,
            maxWidth:    220,
          }}
        >
          {isRecording
            ? `Recording… ${secondsLeft}s`
            : (errorMsg ?? "Tap to retry")}
        </span>
      )}
    </div>
  );
};

export default MicInput;
